<template>
  <div :class="isDashboard ? store.sidebarClass : null">
    <div v-if="ready && questionnaire" id="questionnaire-page" class="content-with-spacing">
      <section class="heading-container" :class="{ dashboard: isDashboard }">
        <div class="heading-content">
          <div class="title-container">
            <h1>{{ questionnaire.name }}</h1>
            <p class="description" v-html="getMarkdown(questionnaire.description)"></p>
            <k-disclaimer :info="true">
              <p>
                <b>Please Note:</b> Questionnaire submissions are not private and may be viewed by our team or senior staff at your organisation. Do not share any private or privileged information in your submission.
              </p>
            </k-disclaimer>
          </div>
          <div v-if="latestSubmission" class="heading-details">
            <k-progress class="score" shape="circle" size="small" :percentage="overallScore" :precision="0" ariaLabel="Questionnaire completion percentage"></k-progress>
            <p class="submission-date">Last submission: <b>{{parseTimestamp(latestSubmission.date_timestamp)}}</b></p>
          </div>
        </div>
      </section>
      <section>
        <empty-placeholder v-if="!latestSubmission && isDashboard" info="The student has not made a submission yet"></empty-placeholder>
        <k-questionnaire v-else
          ref="questionnaire"
          :questionnaire="questionnaire"
          :latest-submission="latestSubmission"
          :read-only="isDashboard"
          :disable-text-toolbar="isDashboard"
          @edited="markEdited"
          >
          <template v-if="!isDashboard" #actions>
            <div class="button-container">
              <button @click.prevent="clear" class="btn btn-secondary reset-button" type="button" title="Reset responses">Reset</button>
              <button @click.prevent="submit" class="btn btn-primary submit-button" :disabled="disableSubmission">Submit Responses</button>
              <quick-add-otj-button v-if="latestSubmission && !isDashboard"></quick-add-otj-button>
            </div>
          </template>
          <template v-if="!isDashboard" #bottom-actions>
            <div class="submit-form-button bottom-submit-button">
              <button @click.prevent="submit" class="btn btn-primary submit-button" :disabled="disableSubmission">Submit Responses</button>
            </div>
          </template>
        </k-questionnaire>
      </section>
    </div>
    <k-modal :show="showLeaveModal" @close="cancelLeave" class="confirm-discard-modal">
      <template #header>
        <h3>You have unsubmitted answers</h3>
      </template>
      <template #body>
        <div class="confirm-discard-modal-body">
          <p>Your answers haven't been submitted and will be lost if you leave.</p>
          <b>Leave this page anyway?</b>
        </div>
      </template>
      <template #footer>
        <button class="modal-return-button btn btn-default cancel-btn" @click="cancelLeave">Stay on page</button>
        <button class="btn btn-danger modal-delete-button" @click="confirmLeave">
          Leave page
        </button>
      </template>
    </k-modal>
  </div>
</template>

<style scoped>
.button-container {
  display: flex;
  justify-content: right;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 15px;
}

.submit-form-button {
  position: relative;
  display: inline-block;
}

.submit-form-button button {
  padding: 10px 25px;
}

.bottom-submit-button {
  gap: 10px;
  display: flex;
  justify-content: center;
  width: 100%;
}

.heading-container {
  display: flex;
  flex-direction: column;
  margin: 15px auto !important;
}

.heading-container.dashboard {
  margin-top: 40px !important;
}

.heading-content {
  display: flex;
  gap: 15px;
  width: 100%;
  margin: 0 auto !important;
  justify-content: space-between;
}

.heading-container .heading-details {
  flex: 1 1 20%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  text-align: right;
}

.title-container {
  align-self: unset;
}

#content .title-container h1 {
  margin: 0 !important;
  align-self: unset;
  padding: 10px 0;
  width: 100%;
}

.heading-details .score {
  width: 100%;
}

@media (max-width: 1024px) {
  .heading-container {
    display: block;
  }

  .heading-container .title-container {
    flex: 1 1 100%;
  }

  #content .heading-container .title-container h1 {
    font-size: 1.8em;
    text-align: left;
  }

  .heading-container .heading-details {
    text-align: center;
  }
}
</style>

<style>
#k-text-editor .ql-editor.ql-blank::before {
  color: var(--kate-type-primary);
  opacity: 0.5;
  left: 7px;
}
</style>

<script>

import useGlobalStore from '@stores/global';
import TimeMixins from '@mixins/time-mixins';
import QuickAddOtjButton from '@base-components/buttons/quick-add-otj-button.vue';
import KProgress from '@base-components/k-progress.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import { markdown } from '@utils/maths-markdown';
import KDisclaimer from '@base-components/k-disclaimer.vue';
import KModal from '@base-components/k-modal.vue';
import KQuestionnaire from './k-questionnaire.vue';

export default {
  mixins: [TimeMixins],

  components: {
    KQuestionnaire,
    KProgress,
    EmptyPlaceholder,
    KDisclaimer,
    QuickAddOtjButton,
    KModal,
  },

  data() {
    return {
      store: useGlobalStore(),
      questionnaireReady: false,
      questionnaire: undefined,
      latestSubmissionReady: false,
      latestSubmission: undefined,
      isSubmitting: false,
      studentName: undefined,
      disableSubmission: true,
      hasUnsubmittedEdits: false,
      showLeaveModal: false,
      leaveNavigationResolve: null,
    };
  },

  beforeRouteLeave() {
    return this.confirmLeaveIfNeeded();
  },

  beforeRouteUpdate() {
    return this.confirmLeaveIfNeeded();
  },

  beforeMount() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    this.$Loading.start();
    this.getLatestSubmission();
    this.getQuestionnaire();
    this.getUserProfile();
  },

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  },

  watch: {
    ready(val) {
      if (val) {
        this.$Loading.finish();
        this.$nextTick(() => {
          this.disableSubmission = false;
        });
      } else {
        this.$Loading.start();
      }
    },
    isSubmitting(val) {
      if (val) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },
  },

  computed: {
    ready() {
      return this.questionnaireReady && this.latestSubmissionReady;
    },
    isDashboard() {
      return this.store.isDashboard;
    },
    moduleQuestionnaireId() {
      return parseInt(this.$route.params.moduleQuestionnaireId, 10);
    },
    studentId() {
      return this.$route.params.studentId;
    },
    overallScore() {
      return this.latestSubmission?.overall_score || 0;
    },
    questionnaireFeedbackEndpoint() {
      if (this.isDashboard) {
        return `/api/stats/users/${this.studentId}/questionnaire/${this.moduleQuestionnaireId}/feedback`;
      }
      return `/api/stats/questionnaire/${this.moduleQuestionnaireId}/feedback`;
    },
    questionnaireMetaEndpoint() {
      if (this.isDashboard) {
        return `/api/curriculum/admin/questionnaire/${this.moduleQuestionnaireId}/users/${this.studentId}`;
      }
      return `/api/curriculum/questionnaire/${this.moduleQuestionnaireId}`;
    },
  },

  methods: {
    markEdited() {
      this.hasUnsubmittedEdits = true;
    },

    confirmLeaveIfNeeded() {
      if (!this.hasUnsubmittedEdits) {
        return true;
      }
      this.showLeaveModal = true;
      return new Promise(resolve => {
        this.leaveNavigationResolve = resolve;
      });
    },

    beforeUnloadHandler(event) {
      if (this.hasUnsubmittedEdits) {
        event.preventDefault();
        event.returnValue = ''; // eslint-disable-line no-param-reassign
      }
    },

    confirmLeave() {
      this.hasUnsubmittedEdits = false;
      this.showLeaveModal = false;
      this.leaveNavigationResolve?.(true);
      this.leaveNavigationResolve = null;
    },

    cancelLeave() {
      this.showLeaveModal = false;
      this.leaveNavigationResolve?.(false);
      this.leaveNavigationResolve = null;
    },

    submit() {
      const payload = this.$refs.questionnaire.getSubmitPayload();
      this.submitQuestionnaire(payload);
    },
    clear() {
      this.disableSubmission = true;
      this.$refs.questionnaire.clearResponses();
      this.$nextTick(() => {
        this.hasUnsubmittedEdits = false;
        this.disableSubmission = false;
      });
    },
    registerCrumbs() {
      let crumbs;
      if (this.isDashboard) {
        crumbs = [
          {
            text: this.studentName,
            path: {
              name: 'dashboard_students',
              params: { studentId: this.studentId },
            },
          },
          { text: this.questionnaire.name, active: true },
        ];
      }
      this.$crumbs.register(crumbs);
    },
    getUserProfile() {
      if (!this.isDashboard) {
        return;
      }
      this.$http.get(`/api/profile/users/${this.studentId}`).then(res => {
        this.studentName = res.data.full_name;
        this.registerCrumbs();
      }).catch(() => {});
    },
    getQuestionnaire() {
      this.$logger.info('Getting questionnaire info', { moduleQuestionnaireId: this.moduleQuestionnaireId }, true);
      this.$http.get(this.questionnaireMetaEndpoint).then(response => {
        this.$logger.info('Got questionnaire info', { moduleQuestionnaireId: this.moduleQuestionnaireId });
        this.questionnaire = response.data;
        this.questionnaireReady = true;
        this.registerCrumbs();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Questionnaire not found', { moduleQuestionnaireId: this.moduleQuestionnaireId }, err);
        } else {
          this.$logger.error('Error getting questionnaire', { moduleQuestionnaireId: this.moduleQuestionnaireId }, err);
          this.$ktoast.showError(err, true);
        }
      });
    },
    getLatestSubmission(updateSidebar) {
      this.$logger.info('Getting latest questionnaire submission', { moduleQuestionnaireId: this.moduleQuestionnaireId }, true);
      this.$http.get(this.questionnaireFeedbackEndpoint).then(response => {
        this.$logger.info('Got latest questionnaire submission', { moduleQuestionnaireId: this.moduleQuestionnaireId });
        this.latestSubmission = response.data.feedback;
        this.previousResponses = this.latestSubmission?.responses;
        // Update the sidebar
        if (updateSidebar) {
          this.$sidebar.updateAssetScore('questionnaire', this.moduleQuestionnaireId, this.latestSubmission.overall_score);
          this.$sidebar.updateCompletionStatus('questionnaire', this.moduleQuestionnaireId, this.latestSubmission.overall_score === 100);
        }
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Latest questionnaire submission not found', { moduleQuestionnaireId: this.moduleQuestionnaireId }, err);
        } else {
          this.$logger.error('Error getting latest questionnaire submission', { moduleQuestionnaireId: this.moduleQuestionnaireId }, err);
          this.$ktoast.showError(err, true);
        }
      }).then(() => {
        this.latestSubmissionReady = true;
      });
    },
    submitQuestionnaire(payload) {
      if (this.isDashboard || this.disableSubmission) {
        return;
      }
      this.isSubmitting = true;
      this.$logger.info('Submitting questionnaire', { moduleQuestionnaireId: this.questionnaire.questionnaire_id }, true);
      this.$http.post(`/api/curriculum/questionnaire/${this.moduleQuestionnaireId}/submit`, payload)
        .then(res => {
          if (res.data.badges_achieved) {
            this.$ktoast.showBadgesAchieved(res.data.badges_achieved);
          }
          this.$ktoast.success('Questionnaire submitted');
          this.hasUnsubmittedEdits = false;
          this.getLatestSubmission(true);
        }).catch(err => {
          // Show submission error even if it is a warning HTTP code
          this.$logger.error('Error submitting questionnaire', { moduleQuestionnaireId: this.moduleQuestionnaireId }, err);
          this.$ktoast.showError(err, true);
        }).then(() => {
          this.isSubmitting = false;
        });
    },
    getMarkdown(str) {
      return markdown(str);
    },
  },
};
</script>
